import React, { useState, useEffect } from 'react';
import { signInWithGoogle } from '../services/firebase';
import { trackReferral, getReferrerInfo } from '../services/api';

const ReferralPage = () => {
  const [step, setStep] = useState('initial');
  const [user, setUser] = useState(null);
  const [referrerInfo, setReferrerInfo] = useState(null);
  const [error, setError] = useState(null);
  const [referrerId, setReferrerId] = useState(null);
  const [processingStatus, setProcessingStatus] = useState('');
  const [referralType, setReferralType] = useState('subscription');

  useEffect(() => {
    const fetchReferrerInfo = async (id) => {
      try {
        const info = await getReferrerInfo(id);
        setReferrerInfo(info);
      } catch (err) {
        setReferrerInfo({
          name: 'Lawbee User',
          email: 'user@lawbee.net',
          rewardAmount: 30,
          rewardType: 'subscription'
        });
      }
    };
    const urlParams = new URLSearchParams(window.location.search);
    const id = urlParams.get('referrerId') || urlParams.get('id');
    const type = urlParams.get('type') || 'subscription';
    if (id) {
      setReferrerId(id);
      setReferralType(type);
      fetchReferrerInfo(id);
    } else {
      setError('Invalid referral link. Please check and try again.');
    }
  }, []);

  const handleGoogleSignIn = async () => {
    try {
      setStep('loading');
      const result = await signInWithGoogle();
      if (!result.success) {
        throw new Error(result.error || 'Authentication failed');
      }
      if (referrerInfo && referrerInfo.email && 
          result.user.email.toLowerCase() === referrerInfo.email.toLowerCase()) {
        setProcessingStatus('self_referral');
        throw new Error('You cannot use your own referral link. Self-referrals are not permitted.');
      }
      const referralCheckResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/referrals/check-duplicate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': process.env.REACT_APP_API_KEY
          },
          body: JSON.stringify({
            referrerId: referrerId,
            userEmail: result.user.email
          })
        }
      );
      if (referralCheckResponse.ok) {
        const checkData = await referralCheckResponse.json();
        if (checkData.alreadyProcessed) {
          setProcessingStatus('already_claimed');
          throw new Error('You have already claimed this referral. Each referral link can only be used once per user.');
        }
        if (checkData.existingUser) {
          setProcessingStatus('already_registered');
          throw new Error('This email is already registered with Lawbee. Referral rewards are only for new users.');
        }
      }
      setUser(result.user);
      if (referrerId) {
        await trackReferral(referrerId, {
          email: result.user.email,
          displayName: result.user.displayName || 'New Lawbee User',
          uid: result.user.uid
        });
        setStep('authenticated');
      } else {
        throw new Error('Invalid referral link. Missing referrer ID.');
      }
    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
      setStep('error');
    }
  };

  const handleDownload = () => {
    setStep('download');
    try {
      localStorage.setItem('pendingWebReferral', JSON.stringify({
        referrerId,
        userEmail: user?.email,
        userName: user?.displayName || 'New Lawbee User',
        userId: user?.uid,
        timestamp: new Date().toISOString(),
        status: 'pending',
        referralType: referralType,
        verified: false
      }));
    } catch (err) {
      console.error('localStorage error:', err);
    }
    setTimeout(() => {
      window.location.href = 'https://play.google.com/store/apps/details?id=in.maxtechno.techno.lawbee';
    }, 2000);
  };

  const getRewardText = () => {
    return `${referrerInfo?.rewardAmount || 30} days premium`;
  };

  const getMaskedName = (name) => {
    if (!name || name.length <= 4) return name || "Someone";
    return name.substring(0, 2) + '*'.repeat(Math.max(1, name.length - 4)) + name.substring(name.length - 2);
  };

  if (step === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-400 via-cyan-500 to-blue-500 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-12 text-center max-w-sm w-full">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Verifying...</h2>
          <p className="text-gray-600 text-sm">Please wait while we verify your account</p>
        </div>
      </div>
    );
  }

  if (step === 'authenticated') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-400 via-cyan-500 to-blue-500 flex items-center justify-center p-4">
        <div className="max-w-sm w-full bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-6 text-white text-center">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h2 className="text-2xl font-bold mb-1">Verified!</h2>
            <p className="text-green-100 text-sm">Ready for Lawbee Premium</p>
          </div>
          <div className="p-6">
            <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-4 mb-6">
              <img 
                src={user?.photoURL || "https://ui-avatars.com/api/?name=User&background=random"} 
                alt="User" 
                className="w-14 h-14 rounded-full" 
              />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-800 truncate">{user?.displayName || "New User"}</p>
                <p className="text-sm text-gray-600 truncate">{user?.email || "user@example.com"}</p>
              </div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
              <h3 className="font-bold text-gray-800 mb-2 text-sm">Success!</h3>
              <p className="text-sm text-gray-700 mb-3">
                <strong>Important:</strong> Sign in with the same Google account in the app to get your {getRewardText()}.
              </p>
              <div className="bg-white rounded-lg p-3 text-xs text-gray-600 space-y-1">
                <div>✓ Download Lawbee app</div>
                <div>✓ Sign in with same Google account</div>
                <div>✓ Get {getRewardText()} automatically</div>
              </div>
            </div>
            <button 
              onClick={handleDownload}
              className="w-full bg-teal-500 hover:bg-teal-600 text-white rounded-xl py-4 flex items-center justify-center gap-3 font-semibold transition-all shadow-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
              </svg>
              Download Lawbee
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'download') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-400 via-cyan-500 to-blue-500 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-12 text-center max-w-sm w-full">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Redirecting...</h2>
          <p className="text-gray-600 text-sm mb-4">Taking you to Google Play Store</p>
          <p className="text-xs text-gray-500">Remember: Use the same Google account in the app!</p>
        </div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-400 via-cyan-500 to-blue-500 flex items-center justify-center p-4">
        <div className="max-w-sm w-full bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-br from-red-500 to-red-600 p-6 text-white text-center">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>
            <h2 className="text-2xl font-bold mb-1">Oops!</h2>
            <p className="text-red-100 text-sm">Something went wrong</p>
          </div>
          <div className="p-6">
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-gray-700">{error || "An error occurred during the referral process. Please try again."}</p>
            </div>
            {processingStatus === 'self_referral' && (
              <div className="bg-gray-50 rounded-xl p-4 mb-6 text-sm text-gray-700">
                <p className="font-semibold mb-2">Why can't I use my own referral link?</p>
                <p className="mb-2">The referral program rewards users who bring new people to Lawbee. Using your own link doesn't qualify.</p>
                <p>Share your referral link with friends and family who are new to Lawbee!</p>
              </div>
            )}
            {processingStatus === 'already_registered' && (
              <div className="bg-gray-50 rounded-xl p-4 mb-6 text-sm text-gray-700">
                <p className="font-semibold mb-2">Already registered?</p>
                <p className="mb-2">This email is already registered with Lawbee. Referral rewards are only for new users.</p>
                <p>Open the app and start your own referral program to earn premium rewards!</p>
              </div>
            )}
            {processingStatus === 'already_claimed' && (
              <div className="bg-gray-50 rounded-xl p-4 mb-6 text-sm text-gray-700">
                <p className="font-semibold mb-2">Already Claimed</p>
                <p className="mb-2">You've already used this referral link. Each link can only be claimed once per user.</p>
                <p>Ask your friends to share their referral links for more premium days!</p>
              </div>
            )}
            <div className="flex gap-3">
              <button 
                onClick={() => setStep('initial')}
                className="flex-1 bg-teal-500 hover:bg-teal-600 text-white rounded-xl py-3 font-semibold transition-all"
              >
                Try Again
              </button>
              <button 
                onClick={() => window.location.href = 'https://play.google.com/store/apps/details?id=in.maxtechno.techno.lawbee'}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl py-3 font-semibold transition-all"
              >
                Download Anyway
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-400 via-cyan-500 to-blue-500 flex items-center justify-center p-4">
      <div className="max-w-sm w-full bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-br from-teal-500 to-cyan-600 p-8 text-center text-white">
          <img 
            src="https://play-lh.googleusercontent.com/9oO1SxLWmx32Za9oOXwFyvDw0iabyS35axHhADICdyJFGerT8GCzbbtMVMk2a1dXVQ=w240-h480-rw" 
            alt="Lawbee" 
            className="w-24 h-24 rounded-3xl mx-auto mb-4 shadow-xl"
          />
          <h1 className="text-3xl font-bold mb-2">You're Invited!</h1>
          <p className="text-teal-50 text-sm">
            {referrerInfo ? (
              <>{getMaskedName(referrerInfo.name)} invited you to join Lawbee</>
            ) : (
              <>Join Lawbee and get premium access</>
            )}
          </p>
        </div>
        <div className="p-6">
          <div className="bg-gradient-to-r from-orange-400 to-orange-500 rounded-2xl p-6 text-white text-center mb-6 shadow-lg">
            <div className="text-4xl mb-2">🎁</div>
            <p className="text-2xl font-bold mb-1">30 Days FREE</p>
            <p className="text-orange-100 text-sm">Premium Access</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <p className="font-semibold text-gray-800 mb-3 text-sm">How it works:</p>
            <div className="space-y-2 text-sm text-gray-700">
              <div className="flex gap-2">
                <span className="font-bold text-teal-600 flex-shrink-0">1.</span>
                <span>Sign in with Google</span>
              </div>
              <div className="flex gap-2">
                <span className="font-bold text-teal-600 flex-shrink-0">2.</span>
                <span>Download the Lawbee app</span>
              </div>
              <div className="flex gap-2">
                <span className="font-bold text-teal-600 flex-shrink-0">3.</span>
                <span>Use the same Google account in app</span>
              </div>
            </div>
          </div>
          <button 
            onClick={handleGoogleSignIn}
            className="w-full bg-white border-2 border-gray-300 hover:border-gray-400 rounded-xl py-4 flex items-center justify-center gap-3 font-semibold text-gray-800 transition-all shadow-sm hover:shadow-md"
          >
            <img 
              src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" 
              alt="Google" 
              className="w-6 h-6" 
            />
            Continue with Google
          </button>
          <p className="text-center text-gray-500 text-xs mt-4">
            Free for new users only
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReferralPage;